import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';
//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Disclaimer from '../../others/Disclaimer';
import FareCalendar from '../../controls/TripFareCalendar/FareCalendar';
import DayCard from '../../items/DayCardMobile/DayCalendarMobile';
import MonthNavigation from '../../controls/TripFareCalendar/atoms/MonthNavigation';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import Skeleton from '../../others/Skeleton/SkeletonFareCalendarOIMobile/SkeletonFareCalendarOIMobile';
import useFiltersDisableSettings from './../InteractiveMapContainer/filters/useFiltersDisabled';
import SecondaryFilters from '../../controls/SecondaryFilters/SecondaryFilters';
import SecondaryFilterCollapsed from '../../controls/SecondaryFilters/SecondaryFiltersCollapsed';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getHistogramsSmart } from '../../../_integrations/everymundo/histogramSmart';
import { getAirports } from '../../../_integrations/everymundo/airports';

//-------------------------------------
// Commons functions
//-------------------------------------
import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useFareItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/InlineFilters/InlineFiltersMobile';
import { getResultsLength } from '../_commons/calendar';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/histogram';
import HeaderContainer from '../../elements/HeaderContainer';
import { generateSecondaryFiltersSettings } from '../_commons/generateSecondaryFiltersSettings';

function LowFareCalendarOIV2ContainerMobile(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    fareCalendar,
    fareItemOnClickAction,
    legend,
    direction,
    initialHistograms,
    getAirports: getAirportsFn,
    getHistogramsSmart: getHistogramsFn,
    showDisclaimer,
    showFilters,
    showTravelClassFilter,
    showSkeletonScreen,
    showLegend,
    skeletonSettings,
    travelClass: travelClassSettings,
    travelClassOptions,
    disclaimerContentSettings,
    mainContentSettings,
    LegendRowSettings,
    legendContentSettings,
    filterContentSettings,
    minInitialResults,
    theme,
    filters,
    showSecondaryFilters = true,
    secondaryFilters,
    secondaryFilterCollapsed,
    thresholdDay,
    // deprecated props
    origin,
    destination,
    disclaimer,
    originDestinationLogic,
    enableSEO = true,
    defaultMonth = 0,
    datalayerTracking = false,
    secondaryFiltersContainerSettings = {}
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const [showThresholdDay, setThresholdDay] = React.useState(!!thresholdDay);
  const [displayErrorView, setDisplayErrorView] = React.useState(false);
  const myStyle = Object.assign({ minHeight: '600px' }, style);
  const combinable = props?.fareCalendar?.combinable ?? false;
  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    availableFilters: showTravelClassFilter
      ? ['OriginDestination', 'TravelClass']
      : ['OriginDestination'],
    containerSettings: filterContentSettings,
    originDestinationSettings: {
      components: {
        column: {
          justifyContent: 'center'
        },
        origin,
        destination
      },
      showOrigin: true,
      showDestination: true
    },
    travelClassSettings: {
      ...travelClassSettings,
      ...travelClassOptions
    },
    clearButtonDisplay: 'hidden',
    ...filters
  };

  const initialMonth = dayjs().add(prePopulation.dateRange.start, 'day').toDate();

  const thresholdDayEnable = Boolean(
    thresholdDay &&
      showThresholdDay &&
      dayjs(initialMonth).endOf('month').diff(dayjs(initialMonth), 'day') + 1 < thresholdDay
  );

  const {
    fares: { match, ...fares },
    retry,
    dispatch,
    tracking,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      pageContext,
      getAirports: getAirportsFn,
      maxItemsAmount: !!defaultMonth ? defaultMonth : 1,
      nextMonth: thresholdDayEnable || !!defaultMonth
    },
    initialData: initialHistograms,
    getFares: getHistogramsFn,
    faresTransformationLogic: 'histogram',
    minInitialResults,
    showNoResultsPlaceholder: false,
    availableFilters: showFilters ? filtersConfig.availableFilters : [],
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency: filtersConfig.hideBudgetOnMultiCurrency,
    routeMode: true,
    getResultsLength,
    showOrigin:
      (filtersConfig.originDestinationSettings &&
        filtersConfig.originDestinationSettings.showOrigin) ||
      secondaryFilters?.originDestinationSettings?.showOrigin,
    showDestination:
      (filtersConfig.originDestinationSettings &&
        filtersConfig.originDestinationSettings.showDestination) ||
      secondaryFilters?.originDestinationSettings?.showDestination,
    enabledhistogram: true,
    datalayerTracking,
    combinable,
    ignoreCurrencyCode: true,
    isMobile: true
  });

  useEffect(() => {
    dispatch({
      type: 'DATE_RANGE_HISTOGRAM_TYPE_CHANGE',
      payload: {
        histogramType: 'OUTBOUND'
      }
    });
    dispatch({
      type: 'MODIFY_ACTION_HISTOGRAM',
      payload: {
        action: '-'
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOriginSelected = Boolean(filtersState.locations.origin.value);
  const isDestinationSelected = Boolean(filtersState.locations.destination.value);
  const secondaryFiltersDisableSettings = useFiltersDisableSettings(
    { ...filters, ...props.secondaryFilters },
    {
      budget: displayErrorView,
      flightType: displayErrorView,
      travelClass: displayErrorView,
      travelerInterest: displayErrorView,
      flightStopCount: displayErrorView,
      redemptionUnit: displayErrorView,
      route: displayErrorView
    }
  );

  const { dateRange } = filtersState;

  const dayCard = useFareItemProps(fareCalendar.dayCard, dispatch, fareItemOnClickAction);

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  const changeMonth = (type, month) => {
    dispatch({ type: type, payload: month });
    setThresholdDay(false);
  };

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  let newDaySettings = { ...dayCard };
  let newSecondaryFilters = { ...secondaryFilters };
  let secondaryFilterCollapsedMobile = {
    ...secondaryFilterCollapsed,
    showExternalClearButton: true
  };
  let newFilters = { ...filtersConfig };
  let newFareCalendar = {
    ...fareCalendar,
    LegendRowSettings,
    legendContentSettings,
    legend,
    showLegend
  };

  if (
    filtersState.redemptionUnit.value === 'POINTS' ||
    filtersState.redemptionUnit.value === 'MILES'
  ) {
    newDaySettings = {
      ...newDaySettings,
      showPrice: false,
      showMiles: true,
      dayCardMobileMiles: {
        ...newDaySettings?.dayCardMobileMiles,
        milesSymbol: filtersState.redemptionUnit.value.toLowerCase()
      }
    };
    newFareCalendar = {
      ...newFareCalendar,
      fareItemAtomSettings: {
        ...newFareCalendar?.fareItemAtomSettings,
        showPrice: false,
        showMiles: true,
        fareItemMiles: {
          ...newFareCalendar?.fareItemAtomSettings?.fareItemMiles,
          milesSymbol: filtersState.redemptionUnit.value.toLowerCase()
        }
      }
    };
    newSecondaryFilters = generateSecondaryFiltersSettings(
      filtersState.redemptionUnit.value,
      newSecondaryFilters
    );
    newFilters = {
      ...newFilters,
      budgetSettings: {
        ...newFilters?.budgetSettings,
        currencyToDisplay: filtersState.redemptionUnit.value
      }
    };
  } else {
    newDaySettings = {
      ...newDaySettings,
      showMiles: false,
      showPrice: true
    };
    newFareCalendar = {
      ...newFareCalendar,
      fareItemAtomSettings: {
        ...newFareCalendar?.fareItemAtomSettings,
        showMiles: false,
        showPrice: true
      }
    };
  }

  const header = (
    <>
      {showSkeletonScreen && match('SKELETON') && (
        <Skeleton {...skeletonSettings} position="absolute" width="100" />
      )}
      <HeaderContainer
        defaultTitleValue={'LOWEST DAILY FARES FOR THE UPCOMING 90 DAYS'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
    </>
  );

  const secondaryFiltersContainerSettingsValue =
    isOriginSelected && isDestinationSelected
      ? secondaryFiltersContainerSettings
      : { ...secondaryFiltersContainerSettings, display: 'flex' };

  const renderSecondaryFilters = showSecondaryFilters && (
    <Box {...secondaryFiltersContainerSettingsValue}>
      <SecondaryFilters
        {...filters}
        {...filtersState}
        {...newSecondaryFilters}
        {...secondaryFiltersDisableSettings}
        filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
      />
      <SecondaryFilterCollapsed
        showClearButton
        {...filters}
        {...filtersState}
        {...{
          ...secondaryFilterCollapsedMobile,
          toggleButtonSettings: {
            activeButton: isOriginSelected && isDestinationSelected ? true : undefined
          },
          disabled: displayErrorView
        }}
        filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
      />
    </Box>
  );

  const renderFareCalendar = ({ data, initialView, errorStatus = false }) => (
    <FareCalendar
      {...newFareCalendar}
      thresholdDayEnable={thresholdDayEnable}
      thresholdDay={thresholdDay}
      defaultMonth={defaultMonth}
      dayCard={newDaySettings}
      initialMonth={initialMonth}
      currentMonth={dateRange.value.start}
      endDateMonth={dateRange.value.end}
      data={data}
      initialView={initialView && !match('LOADING')}
      NavComponentInstance={MonthNavigation}
      onSelectMonth={changeMonth}
      loading={match('LOADING')}
      DayComponentInstance={DayCard}
      isMobile={true}
      filtersState={filtersState}
      globalDispatch={dispatch}
      errorStatus={errorStatus}
      displayErrorView={displayErrorView}
      setDisplayErrorView={setDisplayErrorView}
      combinable={combinable}
      tenantCode={prePopulation?.tenantCode}
    />
  );

  return (
    <InlineFilters
      className={`${className} ${LowFareCalendarOIV2ContainerMobile.componentName}`}
      style={myStyle}
      dataTestId="lowFareCalendarOIV2ContainerMobile"
      header={header}
      {...newFilters}
      {...filtersState}
    >
      {renderSecondaryFilters}

      <Row {...mainContentSettings} style={{ position: 'relative' }}>
        {match('WAITING_FOR_DESTINATION') ||
        !isOriginSelected ||
        !isDestinationSelected ||
        match('ERROR') ? (
          <>
            <Col justifyContent="center" display="flex">
              {renderFareCalendar({
                data: [],
                initialView: !isOriginSelected || !isDestinationSelected,
                errorStatus: match('ERROR')
              })}
            </Col>
          </>
        ) : (
          match('COMPLETE') && (
            <>
              <Col justifyContent="center" display="flex">
                {renderFareCalendar({ data: fares.data, initialView: false, errorStatus: false })}
              </Col>
            </>
          )
        )}
      </Row>
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>
            <Disclaimer labelKey="cmp-low-calendar-container-disclaimer" {...disclaimer} />
          </Col>
        </Row>
      )}
    </InlineFilters>
  );
}

LowFareCalendarOIV2ContainerMobile.propTypes = {
  /**
   * filterContentSettingsRow props
   */
  filterContentSettingsRow: PropTypes.objectOf(PropTypes.any),
  /**
   * headerContentSettings props
   */
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if t container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Destination Component
   */
  destination: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * fareCalendar Component props
   */
  fareCalendar: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Handle the action when click on card
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function for request to obtain histograms
   */
  getHistogramsSmart: PropTypes.func,
  /**
   * headerLocation
   */
  headerLocation: PropTypes.objectOf(PropTypes.any),
  /**
   * Hide the component if the ammount of initial results is lower than this value
   */
  minInitialResults: PropTypes.number,
  /**
   * Used to prepopulate the component
   */
  initialHistograms: PropTypes.arrayOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * legend Component props
   */
  legend: PropTypes.objectOf(PropTypes.any),
  /**
   * legend content settings
   */
  legendContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Origin Component
   */
  origin: PropTypes.objectOf(PropTypes.any),
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Determine if the component will show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * showFilters
   */
  showFilters: PropTypes.bool,
  /**
   * Determine if the component will show the travelClass filter
   */
  showTravelClassFilter: PropTypes.bool,
  /**
   * showCarouselMode
   */
  showCarouselMode: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * showLegend
   */
  showLegend: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Title Component
   */
  title: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * travelClass options
   */
  travelClassOptions: PropTypes.objectOf(PropTypes.any),
  /**
   * travelClass of the component
   */
  travelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * Metadata settings
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * thresholdDay indicate past how many days the low fare calendar must display the upcoming month
   */
  thresholdDay: PropTypes.number
};
LowFareCalendarOIV2ContainerMobile.defaultProps = {
  filterContentSettingsRow: {},
  headerContentSettings: {},
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  containerType: 'stateful',
  destination: {},
  direction: 'ltr',
  disclaimer: {
    fontSize: '3'
  },
  disclaimerContentSettings: {
    marginTop: '3'
  },
  fareCalendar: {},
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  filterContentSettings: {
    marginTop: '2'
  },
  filters: {},
  getAirports,
  getHistogramsSmart,
  headerLocation: {},
  minInitialResults: null,
  initialHistograms: null,
  integrationSettings: {},
  legend: {},
  legendContentSettings: {
    marginTop: '3'
  },
  loading: {},
  mainContentSettings: {
    marginTop: '3'
  },
  noResultsPlaceHolder: {
    bgColor: 'light'
  },
  origin: {},
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: prePopulationDefaultProps,
  showAirportIataCode: true,
  showDisclaimer: true,
  showFilters: true,
  showTravelClassFilter: false,
  showCarouselMode: true,
  showSkeletonScreen: true,
  showLegend: true,
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white', paddingTop: '0.5rem', paddingBottom: '3.25rem' }
  },
  showLoading: true,
  title: {},
  titleKey: 'cmp-low-calendar-container-title',
  translate: () => {},
  travelClassOptions: {
    options: [
      { label: 'economy', value: 'ECONOMY' },
      { label: 'business', value: 'BUSINESS' },
      { label: 'first', value: 'FIRST' }
    ]
  },
  travelClass: {},
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareListLowFareCalendarOI'
  },
  localizationData: null,
  theme: null,
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-low-fare-calendar-sub-title',
  subTitleSettings: {},
  thresholdDay: 0
};
LowFareCalendarOIV2ContainerMobile.componentName = 'LowFareCalendarOIV2ContainerMobile';

// HOC with common functionality to all containers
export default withCommonEMContainer(LowFareCalendarOIV2ContainerMobile);
